import React, { useContext, useEffect, useState } from 'react';
import {
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  Typography, Button
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { FlooringProjectCard } from './subcomponents/FlooringProjectCard';
import { triggerStartNewProjectAnalytics, triggerPageVisitAnalytics } from '../../analytics';
import { cppDataModel } from '../dataModel';
import {
  DESIGN_API_URL,
  DESIGN_STATUS_API_URL,
  DESIGN_API_KEY,
  SEARCH_TYPE,
  PRODUCT_LINE,
  DESIGN_STATUS_CREATED
} from '../../constants';
import { navigateToTop } from '../../../utils/navigate';

export const FlooringProjects = () => {
  const [designs, setDesigns] = useState([]);
  /*
    **IMPORTANT**
    When running locally, useThdCustomer takes your email/svocid from whatever account you're logged into on prod
    To test in stage, overwrite the svocID variable with your svocid from your stage account
  */
  const { svocID } = useThdCustomer() || {};

  const {
    hosts: { customizableProductsBaseUrl = '' },
  } = useContext(ExperienceContext);

  const { data } = useDataModel('getDesignIdsBySearchTerm', {
    variables: {
      searchTerm: svocID,
      searchType: SEARCH_TYPE,
      productLine: PRODUCT_LINE
    },
    ssr: false
  });

  useEffect(() => {
    const fetchDesignsData = async () => {
      const designsData = [];

      if (!data?.getDesignIdsBySearchTerm) {
        return;
      }

      const parsedDataToValidJson = data.getDesignIdsBySearchTerm.replace(/[\d\w-]+/g, (match) => `"${match}"`);
      const designIDsResponse = JSON.parse(parsedDataToValidJson);

      if (designIDsResponse.length) {
        await Promise.all(
          designIDsResponse.map(async (designID) => {
            if (designID) {
              try {
                // Fetch design details
                const designsResponse = await fetch(
                  new Request(`${customizableProductsBaseUrl}/${DESIGN_API_URL}/${designID.toUpperCase()}`),
                  {
                    method: 'GET',
                    headers: new Headers({
                      'X-Api-Key': DESIGN_API_KEY
                    })
                  }
                ).then((response) => response.json());

                // Fetch design status
                const designStatusResponse = await fetch(
                  new Request(`${customizableProductsBaseUrl}/${DESIGN_STATUS_API_URL}/${designID.toUpperCase()}`),
                  {
                    method: 'GET',
                    headers: new Headers({
                      'X-Api-Key': DESIGN_API_KEY
                    })
                  }
                ).then((response) => response.json());

                if (designsResponse?.items?.length) {
                  designsData.push({
                    ...designsResponse,
                    status: designStatusResponse?.status || DESIGN_STATUS_CREATED,
                  });
                }
              } catch (error) {
                console.error(`Error fetching design or status for ${designID}:`, error);
              }
            }
          })
        );
      }
      setDesigns(designsData);
    };

    fetchDesignsData();
  }, [customizableProductsBaseUrl, svocID, data]);

  useEffect(() => {
    triggerPageVisitAnalytics();
  }, []);

  return (
    <>
      <div className="sui-flex sui-flex-col sui-mt-8 sui-justify-between sui-items-center sm:sui-flex-row">
        <div className=" sui-flex sui-gap-2">
          <Typography variant="h1" weight="display">FLOORING PROJECTS</Typography>
          {designs.length > 0 && (
            <Typography variant="h1" weight="display" color="subtle">({designs.length})</Typography>
          )}
        </div>
        {designs.length > 0 && (
          <div className="sui-mt-3 sm:sui-mt-0">
            <Button
              onClick={() => {
                if (typeof window !== 'undefined' && window.location) {
                  triggerStartNewProjectAnalytics();
                  navigateToTop('/services/h/carpet-estimator');
                }
              }}
            >
              Start a Flooring Project
            </Button>
          </div>
        )}
      </div>
      <div className="sui-flex sui-justify-center sui-items-center sui-mt-8 sui-mb-8">
        {designs.length === 0 && (
          <div
            className={
              'sui-flex sui-flex-col sui-items-center sui-text-center sui-mt-8 sui-max-w-lg'
              + ' md:sui-mt-60 md:sui-mb-60'
            }
          >
            <div className="sui-flex sui-flex-col sui-gap-2">
              <Typography
                variant="body-lg"
                weight="bold"
              >
                There&apos;s nothing in here yet
              </Typography>
              <Typography>
                Start shopping for your flooring project now!
              </Typography>
            </div>
            <div className="sui-py-8 sui-w-full">
              <Button
                fullWidth
                onClick={() => {
                  if (typeof window !== 'undefined' && window.location) {
                    triggerStartNewProjectAnalytics();
                    navigateToTop('/services/h/carpet-estimator');
                  }
                }}
              >
                Start a Flooring Project
              </Button>
            </div>
          </div>
        )}
        {designs.length !== 0 && (
          <div
            className={'sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 lg:sui-grid-cols-3 xl:sui-grid-cols-4'
          + ' sui-gap-8 sui-justify-items-center'}
          >
            {designs.map(({ items, design, status }, index) => {
              return (
                <FlooringProjectCard
                  key={index}
                  items={items}
                  designId={design.designId}
                  designIndex={index}
                  status={status}
                />
              );
            })}
          </div>
        )}
      </div>
    </>

  );
};

FlooringProjects.displayName = 'FlooringProjects';

FlooringProjects.dataModel = cppDataModel;